import React, { useState } from "react";
import Card from "components/card";
import { getPaymentPortalUrl } from "api/dashboard";
import { MdPayment, MdReceipt, MdError, MdRefresh } from "react-icons/md";

const PaymentInfo = ({ invoices, isLoading, error }) => {
  const [portalUrl, setPortalUrl] = useState("");
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (amount, currency) => {
    if (amount === undefined || amount === null) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  };

  const handleUpdatePayment = async () => {
    setIsPortalLoading(true);
    setPortalError(null);

    try {
      const response = await getPaymentPortalUrl();
      const url = response.data.hostedUrl;
      setPortalUrl(url);
      
      if (url) {
        window.open(url, "_blank");
      } else {
        setPortalError("No payment portal URL received");
      }
    } catch (err) {
      console.error("Failed to get payment portal URL", err);
      setPortalError("Failed to open payment portal. Please try again.");
    } finally {
      setIsPortalLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card extra="!p-[20px] text-center h-full">
        <div className="animate-pulse flex flex-col gap-4">
          <div className="h-6 bg-navy-700 rounded w-3/4 mx-auto"></div>
          <div className="h-4 bg-navy-700 rounded w-full mx-auto"></div>
          <div className="h-4 bg-navy-700 rounded w-full mx-auto"></div>
          <div className="h-4 bg-navy-700 rounded w-full mx-auto"></div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card extra="!p-[20px] text-center h-full">
        <div className="text-red-400 flex flex-col items-center">
          <MdError size={40} className="mb-2" />
          <div>Error loading payment data</div>
          <button 
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded flex items-center justify-center gap-2" 
            onClick={() => window.location.reload()}
          >
            <MdRefresh className="h-5 w-5" />
            Retry
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card extra="!p-[20px] h-full dark:!bg-navy-800">
      <div className="mb-8 w-full">
        <h4 className="text-xl font-bold text-navy-700 dark:text-white">
          Payment Information
        </h4>
        <p className="mt-2 text-base text-gray-600 dark:text-gray-400">
          Manage your payment methods and view past invoices
        </p>
      </div>

      <div className="mb-6">
        <button
          onClick={handleUpdatePayment}
          disabled={isPortalLoading}
          className={`flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200 w-full ${
            isPortalLoading ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          <MdPayment className="h-5 w-5" />
          {isPortalLoading ? "Loading..." : "Update Payment Method"}
        </button>
        {portalError && (
          <div className="mt-2 text-sm text-red-500 p-2 border border-red-300 rounded bg-red-50 dark:bg-red-900/20 dark:border-red-800">
            <p className="flex items-center gap-1">
              <MdError className="h-4 w-4" />
              {portalError}
            </p>
            <p className="mt-1 text-xs">Please try again or contact support if the problem persists.</p>
          </div>
        )}
      </div>

      <div>
        <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white flex items-center">
          <MdReceipt className="mr-2" /> Invoice History
        </h5>
        
        {invoices && invoices.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-navy-700">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:bg-navy-800 dark:divide-gray-700">
                {invoices.map((invoice, index) => (
                  <tr key={invoice.invoiceId || index} className="hover:bg-gray-50 dark:hover:bg-navy-700">
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(invoice.date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {formatPrice(invoice.amount, invoice.currency)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        invoice.status === 'paid' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-navy-900">
            <MdReceipt className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-gray-500 dark:text-gray-400">No invoice history available</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default PaymentInfo;
